import { createSelector } from "@reduxjs/toolkit";
import { omit } from "lodash";
import { isPlainObject } from "lodash";

import equal from "@superblocksteam/fast-deep-equal/es6";
import { TriggerStepType, WidgetTypes } from "@superblocksteam/shared";

import { PAGE_WIDGET_ID } from "legacy/constants/WidgetConstants";
import { getWidgets } from "legacy/selectors/entitiesSelector";
import { getSingleSelectedWidgetId } from "legacy/selectors/sagaSelectors";
import type { WidgetProps } from "legacy/widgets";
import type {
  KeyValueProperty,
  KeyValueWidgetProps,
} from "legacy/widgets/KeyValueWidget/types";
import type { ManualMenuItem } from "legacy/widgets/MenuWidget/types";
import type { ColumnProperties } from "legacy/widgets/TableWidget/TableComponent/Constants";
import type { TableWidgetProps } from "legacy/widgets/TableWidget/TableWidgetConstants";
import {
  getColumnIdFromAi,
  getKvPropertyIdFromAi,
} from "store/slices/ai/columnUtils";
import type { AppState } from "store/types";

import { getMenuItemId } from "./menuUtils";
import type {
  AddEventAction,
  ClarkAction,
  RemoveColumnAction,
  RemoveEventAction,
  SetColumnAction,
  RemoveKvItemAction,
  SetKvPropertyAction,
} from "./types";

export interface ColumnChange {
  columnId: string;
  action: SetColumnAction | RemoveColumnAction;
  column: ColumnProperties;
  aiColumnId: string;
}

export interface PropertyChange {
  propertyId: string;
  action: SetKvPropertyAction | RemoveKvItemAction;
  property: KeyValueProperty;
  aiPropertyId: string;
}

export const selectAiState = (state: AppState) => state.ai;

export const selectIsEditingWithAi = createSelector(selectAiState, (state) => {
  return state.actionsRequestId !== undefined;
});

export const selectAiModalOpen = createSelector(
  selectAiState,
  (state) => state.focusedEntityId !== undefined && state.viewMode === "modal",
);

export const selectInitialPosition = createSelector(
  selectAiState,
  (state) => state.initialPosition,
);

export const selectAiFocusedEntityId = createSelector(
  selectAiState,
  (state) => state.focusedEntityId,
);

export const selectAiApiStatuses = createSelector(
  selectAiState,
  (state) => state.apiStatuses,
);

export const selectAiWidgetChangesById = createSelector(
  selectAiState,
  (state) => {
    if (!state.focusedEntityId || !state.widgetChanges) {
      return undefined;
    }
    return state.widgetChanges as Record<
      string,
      Partial<Omit<WidgetProps, "children">>
    >;
  },
);

export const selectAddedRemovedUpdatedTableColumns = createSelector(
  [selectAiState, getWidgets, (_state: AppState, widgetId: string) => widgetId],
  (
    aiState,
    widgets,
    widgetId,
  ): {
    addedColumns: ColumnChange[];
    removedColumns: ColumnChange[];
    updatedColumns: ColumnChange[];
  } => {
    const emptyResult = {
      addedColumns: [],
      removedColumns: [],
      updatedColumns: [],
    };
    if (!widgetId || !aiState.widgetsAtRequestStart) {
      return emptyResult;
    }

    const _previousWidget = aiState.widgetsAtRequestStart?.[widgetId];
    const _currentWidget = widgets[widgetId];
    if (
      !aiState.actions?.length ||
      !_currentWidget ||
      _currentWidget.type !== WidgetTypes.TABLE_WIDGET
    ) {
      return emptyResult;
    }

    const currentWidget = _currentWidget as TableWidgetProps;
    const widgetAtRequestStart = _previousWidget as TableWidgetProps;
    const existingColumns = Object.keys(
      widgetAtRequestStart?.primaryColumns ?? {},
    );
    const setActions = (aiState.actions || []).filter(
      (action: ClarkAction) =>
        action?.action === "set" &&
        "table_column" in action &&
        action?.table_column != null,
    );
    // Added columns with their actions
    const addedColumns = setActions
      .filter((action) => {
        const columnId = getColumnIdFromAi(
          (action as SetColumnAction).table_column,
          widgetAtRequestStart?.primaryColumns ?? {},
        );
        return !existingColumns.includes(columnId);
      })
      .map((action) => {
        const columnId = (action as SetColumnAction).table_column;
        const primaryColumns = currentWidget?.primaryColumns as
          | Record<string, ColumnProperties>
          | undefined;
        const column = primaryColumns?.[columnId];
        if (!column) return undefined;
        return {
          columnId,
          action,
          column,
          aiColumnId: columnId,
        };
      })
      .filter((item): item is ColumnChange => item !== undefined);

    // Updated columns with their actions
    const updatedColumns = setActions
      .filter((action) => {
        const columnId = getColumnIdFromAi(
          (action as SetColumnAction).table_column,
          widgetAtRequestStart?.primaryColumns ?? {},
        );
        return existingColumns.includes(columnId);
      })
      .map((action) => {
        const columnId = getColumnIdFromAi(
          (action as SetColumnAction).table_column,
          widgetAtRequestStart?.primaryColumns ?? {},
        );
        const primaryColumns = currentWidget?.primaryColumns as
          | Record<string, ColumnProperties>
          | undefined;
        const column = primaryColumns?.[columnId];
        if (!column) return undefined;
        return {
          columnId,
          action,
          column,
          aiColumnId: (action as SetColumnAction).table_column,
        };
      })
      .filter((item): item is ColumnChange => item !== undefined);

    const removeActions = (aiState.actions || []).filter(
      (action: ClarkAction) =>
        action?.action === "remove" &&
        "table_column" in action &&
        action.table_column != null,
    );

    // Removed columns with their actions
    const removedColumns = widgetAtRequestStart
      ? removeActions
          .map((action) => {
            const columnId = getColumnIdFromAi(
              (action as RemoveColumnAction).table_column,
              widgetAtRequestStart.primaryColumns,
            );

            if (
              !columnId ||
              !widgetAtRequestStart?.primaryColumns?.[columnId]
            ) {
              return undefined;
            }
            // Skip non-derived columns as they just get hidden and not deleted
            if (!widgetAtRequestStart.primaryColumns[columnId].isDerived) {
              const primaryColumns = currentWidget?.primaryColumns as
                | Record<string, ColumnProperties>
                | undefined;
              const column = primaryColumns?.[columnId];
              // add this to the updated columns instead, because a removal of a non-derived column is actually a set of isVisible to false
              if (column) {
                updatedColumns.push({
                  columnId,
                  aiColumnId: (action as RemoveColumnAction).table_column,
                  column,
                  action: {
                    action: "set",
                    property: "isVisible",
                    value: false,
                    table_column: (action as RemoveColumnAction).table_column,
                  } as SetColumnAction,
                });
              }
              return undefined;
            }
            const column = widgetAtRequestStart.primaryColumns[columnId];
            return {
              columnId,
              action,
              column,
              aiColumnId: (action as RemoveColumnAction).table_column,
            };
          })
          .filter((item): item is ColumnChange => item !== undefined)
      : [];

    return {
      addedColumns,
      removedColumns,
      updatedColumns,
    };
  },
);

export const selectAddedRemovedUpdatedKeyValueProperties = createSelector(
  [selectAiState, getWidgets, (_state: AppState, widgetId: string) => widgetId],
  (
    aiState,
    widgets,
    widgetId,
  ): {
    addedProperties: PropertyChange[];
    removedProperties: PropertyChange[];
    updatedProperties: PropertyChange[];
  } => {
    const emptyResult = {
      addedProperties: [],
      removedProperties: [],
      updatedProperties: [],
    };
    if (!widgetId) {
      return emptyResult;
    }

    const currentWidget = widgets[widgetId] as KeyValueWidgetProps;
    const _previousWidget = aiState.widgetsAtRequestStart?.[widgetId];
    if (
      !aiState.actions?.length ||
      !currentWidget ||
      currentWidget.type !== WidgetTypes.KEY_VALUE_WIDGET
    ) {
      return emptyResult;
    }

    const existingProperties = _previousWidget
      ? (_previousWidget as KeyValueWidgetProps).properties
      : {};
    const currentProperties = currentWidget.properties;

    const setActions = (aiState.actions || []).filter(
      (action: ClarkAction) =>
        action?.action === "set" &&
        "key_value_property" in action &&
        action?.key_value_property != null,
    );

    // Added properties with their actions
    const addedProperties = setActions
      .filter((action) => {
        const propertyId = getKvPropertyIdFromAi(
          (action as SetKvPropertyAction).key_value_property,
          existingProperties,
        );
        return !existingProperties[propertyId];
      })
      .map((action) => {
        const propertyId = (action as SetKvPropertyAction).key_value_property;
        const property = currentProperties?.[propertyId];
        if (!property) return undefined;
        return {
          propertyId,
          action,
          property,
          aiPropertyId: propertyId,
        };
      })
      .filter((item): item is PropertyChange => item !== undefined);

    const updatedProperties = setActions
      .filter((action) => {
        const propertyId = getKvPropertyIdFromAi(
          (action as SetKvPropertyAction).key_value_property,
          existingProperties,
        );
        return existingProperties[propertyId];
      })
      .map((action) => {
        const propertyId = getKvPropertyIdFromAi(
          (action as SetKvPropertyAction).key_value_property,
          existingProperties,
        );
        const property = currentProperties?.[propertyId];
        if (!property) return undefined;
        return {
          propertyId,
          action,
          property,
          aiPropertyId: propertyId,
        };
      })
      .filter((item): item is PropertyChange => item !== undefined);

    const removeActions = (aiState.actions || []).filter(
      (action: ClarkAction) =>
        action?.action === "remove" &&
        "key_value_property" in action &&
        action.key_value_property != null,
    );

    // Removed properties with their actions
    const removedProperties = removeActions
      .map((action) => {
        const propertyId = getKvPropertyIdFromAi(
          (action as RemoveKvItemAction).key_value_property,
          existingProperties,
        );
        if (!propertyId || !existingProperties[propertyId]) {
          return undefined;
        }
        // Skip non-derived properties as they just get hidden and not deleted
        if (!existingProperties[propertyId].isDerived) {
          return undefined;
        }
        const property = existingProperties[propertyId];
        return {
          propertyId,
          action,
          property: property as KeyValueProperty,
          aiPropertyId: (action as RemoveKvItemAction).key_value_property,
        };
      })
      .filter((item): item is PropertyChange => item !== undefined);

    return {
      addedProperties,
      removedProperties,
      updatedProperties,
    };
  },
);

export const selectAddedRemovedEventTriggers = createSelector(
  [
    selectAiState,
    getWidgets,
    (_state: AppState, widgetId?: string) => widgetId,
    (_state: AppState, _widgetId?: string, propertyName?: string) =>
      propertyName,
  ],
  (
    aiState,
    widgets,
    widgetId,
    propertyName,
  ): { addedTriggers: any[]; removedTriggers: any[] } => {
    const emptyResult = {
      addedTriggers: [],
      removedTriggers: [],
    };
    if (!widgetId || !propertyName) {
      return emptyResult;
    }

    const selectedWidget = widgets[widgetId];
    const previousWidget = aiState.widgetsAtRequestStart?.[widgetId];

    if (
      !selectedWidget ||
      !aiState.actions ||
      !aiState.actions?.length ||
      !aiState.widgetChanges
    ) {
      return emptyResult;
    }

    const addedEventTriggers = aiState.actions
      .filter((action) => {
        const isAddTriggerAction =
          action.action === "add" &&
          action.property === propertyName &&
          action.value &&
          isPlainObject(action.value) &&
          Object.values(TriggerStepType).includes(
            ((action as AddEventAction).value as any).type as TriggerStepType,
          );

        return isAddTriggerAction;
      })
      .map((addTriggerAction) => {
        return (
          (selectedWidget?.[
            propertyName as keyof typeof selectedWidget
          ] as any[]) || []
        ).find((dataTreeTrigger: any) => {
          return (
            dataTreeTrigger.type ===
              ((addTriggerAction as AddEventAction).value as any).type &&
            equal(
              omit(dataTreeTrigger, "id"),
              (addTriggerAction as AddEventAction).value,
            )
          );
        });
      })
      .filter((trigger) => Boolean(trigger));

    const removedEventTriggers = (aiState.actions || [])
      .filter((action) => {
        const isRemoveTriggerAction =
          action.action === "remove" &&
          "property" in action &&
          action.property === propertyName &&
          "value" in action;

        return isRemoveTriggerAction;
      })
      .map((removeTriggerAction) => {
        const removeId = (removeTriggerAction as RemoveEventAction)?.value?.id;
        const propertyValue =
          previousWidget?.[propertyName as keyof WidgetProps] ?? [];

        if (!removeId || !propertyValue || !Array.isArray(propertyValue)) {
          return undefined;
        }

        return propertyValue.find((trigger: any) => trigger?.id === removeId);
      })
      .filter((trigger) => Boolean(trigger));

    return {
      addedTriggers: addedEventTriggers,
      removedTriggers: removedEventTriggers,
    };
  },
);

export const selectAddedRemovedProperty = createSelector(
  [
    selectAiState,
    getWidgets,
    (_state: AppState, widgetId: string) => widgetId,
    (_state: AppState, _widgetId?: string, propertyName?: string) =>
      propertyName,
  ],
  (
    aiState,
    widgets,
    widgetId,
    propertyName,
  ):
    | {
        tag: "added" | "removed" | undefined;
        originalValue: any;
      }
    | undefined => {
    if (!propertyName || !aiState.actions) {
      return undefined;
    }

    const selectedWidget = widgets[widgetId];
    if (!selectedWidget) {
      return undefined;
    }

    if (
      aiState.actions.some(
        (action) => action.action === "add" && action.property === propertyName,
      )
    ) {
      return {
        tag: "added",
        originalValue: null,
      };
    }

    if (
      aiState.actions.some(
        (action) =>
          action.action === "remove" &&
          "property" in action &&
          action.property === propertyName,
      )
    ) {
      return {
        tag: "removed",
        originalValue: selectedWidget[propertyName as keyof WidgetProps],
      };
    }

    return undefined;
  },
);

export const selectAddedUpdatedRemovedChildren = createSelector(
  [selectAiState, getWidgets, (_state: AppState, widgetId: string) => widgetId],
  (
    aiState,
    widgets,
    widgetId,
  ): {
    addedItems: NestedItemChange[];
    removedItems: NestedItemChange[];
    updatedItems: NestedItemChange[];
  } => {
    const emptyResult = {
      addedItems: [],
      removedItems: [],
      updatedItems: [],
    };
    const editedWidget = widgetId ? widgets[widgetId] : undefined;
    const widgetsAtRequestStart = aiState.widgetsAtRequestStart;

    if (!widgetsAtRequestStart || !editedWidget || !aiState.actions?.length) {
      return emptyResult;
    }

    const originalChildren =
      (widgetsAtRequestStart[widgetId] as any)?.children || [];
    const editedChildren = (editedWidget as any).children || [];
    const originalTabs = (widgetsAtRequestStart[widgetId] as any)?.tabs || [];
    const editedTabs = (editedWidget as any).tabs || [];
    const idsToIndices =
      aiState.metadataByWidgetId?.[widgetId]?.idsToIndices ?? {};
    const defaultName =
      editedWidget.type === WidgetTypes.TABS_WIDGET ? "Tab" : "Column";

    const addedItems: NestedItemChange[] = editedChildren
      .map((id: string, index: number) => {
        const label = editedTabs[index]?.label ?? `${defaultName} ${index}`;
        return {
          currentIndex: index,
          id,
          indexFromAi: idsToIndices[id] ?? index,
          label,
        };
      })
      .filter(({ id }: { id: string }) => {
        return !originalChildren.some((ogChild: string) => ogChild === id);
      });

    const removedItems: NestedItemChange[] = originalChildren
      .filter((ogChild: string) => {
        return !editedChildren.some((child: string) => child === ogChild);
      })
      .map((child: string, index: number) => {
        return {
          indexFromAi: idsToIndices[child] ?? -1,
          currentIndex: -1,
          id: child,
          label: `${originalTabs[idsToIndices[child]]?.label ?? defaultName}`,
        };
      });

    const updatedItems: NestedItemChange[] = editedChildren
      .map((id: string, index: number) => {
        return {
          currentIndex: index,
          id,
          indexFromAi: idsToIndices[id] ?? index,
          label: `${editedTabs[index]?.label ?? defaultName} ${index}`,
        };
      })
      .filter(({ id, indexFromAi }: { id: string; indexFromAi: number }) => {
        const existedBefore = originalChildren.some(
          (originalIndex: string) => originalIndex === id,
        );
        const wasEdited = (aiState.actions ?? []).some(
          (action) =>
            "tabs_tab" in action &&
            String(action.tabs_tab) === String(indexFromAi),
        );
        return existedBefore && wasEdited;
      });

    return {
      addedItems,
      removedItems,
      updatedItems,
    };
  },
);

export type NestedItemChange = {
  indexFromAi: number;
  currentIndex: number;
  label: string;
  id: string;
};

export const selectAddedUpdatedRemovedNestedChildListItems = createSelector(
  [selectAiState, getWidgets, (_state: AppState, widgetId: string) => widgetId],
  (
    aiState,
    widgets,
    widgetId,
  ): {
    addedItems: NestedItemChange[];
    removedItems: NestedItemChange[];
    updatedItems: NestedItemChange[];
  } => {
    const emptyResult = {
      addedItems: [],
      removedItems: [],
      updatedItems: [],
    };
    const editedWidget = widgetId ? widgets[widgetId] : undefined;
    const widgetsAtRequestStart = aiState.widgetsAtRequestStart;

    if (!widgetsAtRequestStart || !editedWidget || !aiState.actions?.length) {
      return emptyResult;
    }

    const originalMenuItems =
      (widgetsAtRequestStart[widgetId] as any)?.manualChildren || [];
    const editedMenuItems = (editedWidget as any).manualChildren || [];
    const idsToIndices =
      aiState.metadataByWidgetId?.[widgetId]?.idsToIndices ?? {};

    const addedItems: NestedItemChange[] = editedMenuItems
      .map((child: ManualMenuItem, index: number) => {
        const id = getMenuItemId(child);
        return {
          currentIndex: index,
          id,
          indexFromAi: idsToIndices[id] ?? index,
          label: (child as any).label ?? `Menu Item ${index}`,
        };
      })
      .filter(({ id }: { id: string }) => {
        return !originalMenuItems.some(
          (ogChild: ManualMenuItem) => getMenuItemId(ogChild) === id,
        );
      });

    const removedItems: NestedItemChange[] = originalMenuItems
      .filter((ogChild: ManualMenuItem) => {
        return !editedMenuItems.some(
          (child: ManualMenuItem) =>
            getMenuItemId(ogChild) === getMenuItemId(child),
        );
      })
      .map((child: ManualMenuItem) => {
        const id = getMenuItemId(child);
        return {
          indexFromAi: idsToIndices[id] ?? -1,
          currentIndex: -1,
          id,
          label: (child as any).label ?? `Menu Item`,
        };
      });

    const updatedItems: NestedItemChange[] = editedMenuItems
      .map((child: ManualMenuItem, index: number) => {
        const id = getMenuItemId(child);
        return {
          currentIndex: index,
          id,
          indexFromAi: idsToIndices[id] ?? index,
          label: (child as any).label ?? `Menu Item ${index}`,
          child,
        };
      })
      .filter(({ id, child }: { id: string; child: ManualMenuItem }) => {
        return originalMenuItems.some(
          (ogChild: ManualMenuItem) =>
            getMenuItemId(ogChild) === id && !equal(ogChild, child),
        );
      });
    return {
      addedItems,
      removedItems,
      updatedItems,
    };
  },
);

/**
 * For now, the Page component doesn't support prompting via Clark.
 * So we can't use the Page as the default selected component.
 * This selector will return the first child of the Page widget if its a section widget.
 *
 * TODO: we should remove this function once Page supports prompting.
 */
export const selectAiChatDefaultWidgetId = createSelector(
  [getWidgets, getSingleSelectedWidgetId],
  (widgets, selectedWidgetId) => {
    if (
      selectedWidgetId &&
      selectedWidgetId !== PAGE_WIDGET_ID &&
      widgets[selectedWidgetId]
    ) {
      return selectedWidgetId;
    }

    const page = widgets[PAGE_WIDGET_ID];
    if (!page) {
      console.error("Missing page component in canvas");
      return undefined;
    }

    const firstSectionChild = page.children?.find(
      (child) => widgets[child]?.type === WidgetTypes.SECTION_WIDGET,
    );
    if (!firstSectionChild) {
      console.error("Page doesn't have any valid section components", page);
      return undefined;
    }

    return firstSectionChild;
  },
);
